// 桌面右键菜单。
//
// 目前只服务一个动作:在 App 图标上右键 →「移入新建文件夹」。这是显式建文件夹
// 的唯一入口 —— 在此之前只能靠"把一个 App 拖到另一个 App 上"合并出文件夹,
// 用户反馈找不到建文件夹的地方。
//
// 为什么不做"桌面空白右键 → 新建文件夹":后端 reconcile_layout 会删掉空
// folder(见 createFolderWithApp 注释),空文件夹存不住,所以建文件夹必须从
// 一个具体 App 出发。

import { useEffect, useRef } from 'react';

export interface ContextMenuState {
  x: number;
  y: number;
  appId: string;
}

interface DesktopContextMenuProps {
  state: ContextMenuState;
  label: string;
  onSelect: (appId: string) => void;
  onClose: () => void;
}

export function DesktopContextMenu({
  state,
  label,
  onSelect,
  onClose,
}: DesktopContextMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  // 打开即聚焦首项,方便键盘操作;ESC / 点外部 / 滚动都关闭。
  useEffect(() => {
    ref.current?.querySelector('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    window.addEventListener('keydown', onKey);
    // capture:菜单项自己的 click 先跑,再由这里兜底关闭。
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onClose, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  // 贴边翻转:菜单宽约 160px、单项高约 34px,靠近右/下边界时往回收,避免被裁掉。
  const width = 160;
  const height = 42;
  const left =
    typeof window !== 'undefined' && state.x + width > window.innerWidth
      ? Math.max(0, state.x - width)
      : state.x;
  const top =
    typeof window !== 'undefined' && state.y + height > window.innerHeight
      ? Math.max(0, state.y - height)
      : state.y;

  return (
    <div
      ref={ref}
      role="menu"
      aria-label="桌面右键菜单"
      data-testid="desktop-context-menu"
      className="os-context-menu"
      style={{ left, top }}
    >
      <button
        type="button"
        role="menuitem"
        className="os-context-menu__item"
        onClick={() => {
          onSelect(state.appId);
          onClose();
        }}
      >
        {label}
      </button>
    </div>
  );
}
